import * as React from "react"
import { cn } from "@/lib/utils"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    const variants = {
      default: "bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:shadow-md active:scale-[0.98]",
      destructive: "bg-red-500 text-white shadow-sm hover:bg-red-600 active:scale-[0.98]",
      outline: "border border-slate-200 bg-white shadow-sm hover:bg-slate-100 hover:text-slate-900 active:scale-[0.98]",
      secondary: "bg-slate-100 text-slate-900 shadow-sm hover:bg-slate-200 active:scale-[0.98]",
      ghost: "hover:bg-slate-100 hover:text-slate-900",
      link: "text-primary underline-offset-4 hover:underline",
    }
    const sizes = {
      default: "h-10 px-4 py-2",
      sm: "h-8 rounded-lg px-3 text-xs",
      lg: "h-12 rounded-xl px-8",
      icon: "h-10 w-10",
    }

    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center rounded-xl text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

function buttonVariants({ variant = "default", size = "default" }: { variant?: ButtonProps["variant"]; size?: ButtonProps["size"] } = {}) {
  const variants = {
    default: "bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:shadow-md active:scale-[0.98]",
    destructive: "bg-red-500 text-white shadow-sm hover:bg-red-600 active:scale-[0.98]",
    outline: "border border-slate-200 bg-white shadow-sm hover:bg-slate-100 hover:text-slate-900 active:scale-[0.98]",
    secondary: "bg-slate-100 text-slate-900 shadow-sm hover:bg-slate-200 active:scale-[0.98]",
    ghost: "hover:bg-slate-100 hover:text-slate-900",
    link: "text-primary underline-offset-4 hover:underline",
  }
  const sizes = {
    default: "h-10 px-4 py-2",
    sm: "h-8 rounded-lg px-3 text-xs",
    lg: "h-12 rounded-xl px-8",
    icon: "h-10 w-10",
  }
  return `inline-flex items-center justify-center rounded-xl text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${variants[variant!]} ${sizes[size!]}`
}

export { Button, buttonVariants }
